import { ButtonBase } from "@mui/material";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { Skeleton } from "@/components/Skeleton/Skeleton";
import { ImageCarousel } from "@/features/trips/components/ImageCarousel";
import { TripTag } from "@/features/trips/components/TripTag";
import type { Trip } from "@/features/trips/types/types";
import { formatCurrency } from "@/utils/currency";

type Props = {
  trip: Trip;
  onClick: () => void;
};

export const TripCard = ({ trip, onClick }: Props) => {
  const intl = useIntl();

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        padding: "calc(var(--spacing))",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-3xl)",
        display: "block",
        width: "100%",
        textAlign: "initial",
      }}
    >
      <ImageCarousel
        title={trip.name}
        subtitle={trip.subtitle}
        imagesUrls={trip.imageUrls}
        tags={trip.tags.map((tag) => (
          <TripTag key={tag} title={tag} />
        ))}
      />

      <div className="flex items-center justify-between gap-2 rounded-3xl p-4">
        <div className="text-text-primary">
          <div className="text-base font-bold">
            {trip.price !== trip.discountedPrice ? (
              <div className="text-sm font-medium text-semantic-error-strong line-through">
                {formatCurrency(trip.price)}
              </div>
            ) : null}
            {intl.formatMessage(
              { id: "trips.priceFrom", defaultMessage: "From {price}" },
              {
                price: formatCurrency(trip.discountedPrice),
              },
            )}
          </div>
          <div className="text-xs font-medium">
            {intl.formatMessage({
              id: "trips.perPerson",
              defaultMessage: "per person",
            })}{" "}
            <span className="text-[#D9D9D9]">&bull;</span>{" "}
            {intl.formatMessage(
              {
                id: "trips.duration",
                defaultMessage:
                  "{nights} {nights, plural, one {night} other {nights}} / {days} {days, plural, one {day} other {days}}",
              },
              {
                nights: trip.durationInDays - 1,
                days: trip.durationInDays,
              },
            )}
          </div>
        </div>

        <div className="shrink-0">
          <Button as="span" size="small" variant="outlined">
            {intl.formatMessage({
              id: "trips.bookNow",
              defaultMessage: "Book now",
            })}
          </Button>
        </div>
      </div>
    </ButtonBase>
  );
};

export const TripCardSkeleton = () => {
  return (
    <div className="rounded-3xl bg-white p-1">
      <div className="flex aspect-7/5 shrink-0 overflow-clip rounded-t-2xl">
        <Skeleton width="100%" height="100%" variant="rectangular" />
      </div>

      <div className="flex items-center justify-between gap-2 p-4">
        <div className="flex grow-1 flex-col gap-1">
          <Skeleton width="50%" height={20} />
          <Skeleton width="70%" height={16} />
        </div>

        <Skeleton width={100} height={36} pill />
      </div>
    </div>
  );
};
